import { AxiosError } from 'axios';
import api from './api';
import { getEntityId } from '../utils/entityId';

export function getApiErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof AxiosError) {
    const data = error.response?.data as { message?: string; error?: string; errors?: Record<string, string> } | undefined;
    if (data?.message) return data.message;
    if (data?.error && typeof data.error === 'string') return data.error;
    if (data?.errors) {
      const first = Object.values(data.errors)[0];
      if (first) return first;
    }
    return error.message || fallback;
  }
  if (error instanceof Error) return error.message;
  return fallback;
}

export type CategoryPayload = {
  name: string;
  description?: string;
  imageUrl?: string;
  parent?: string | null;
  isActive?: boolean;
  attributes?: Array<{ attribute: string; isRequired?: boolean; displayOrder?: number }>;
};

export type BrandPayload = {
  name: string;
  description?: string;
  logoUrl?: string;
  website?: string;
  isActive?: boolean;
};

export type AttributePayload = {
  name: string;
  displayName: string;
  type: 'select' | 'color' | 'text';
  description?: string;
  iconUrl?: string;
  isRequired?: boolean;
  isActive?: boolean;
  displayOrder?: number;
};

const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
const MAX_IMAGE_SIZE_BYTES = 5 * 1024 * 1024;

export function validateAdminImageFile(file: File): string | null {
  if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
    return 'Only JPEG, PNG, GIF, and WebP images are allowed';
  }
  if (file.size > MAX_IMAGE_SIZE_BYTES) {
    return 'Max file size is 5MB';
  }
  return null;
}

export async function uploadAdminImage(file: File): Promise<string> {
  const validationError = validateAdminImageFile(file);
  if (validationError) {
    throw new Error(validationError);
  }

  const formData = new FormData();
  formData.append('image', file);
  const response = await api.post('/upload-single-file', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  const fileUrl = response.data?.data?.fileUrl as string | undefined;
  if (!fileUrl) {
    throw new Error('Invalid upload response');
  }
  return fileUrl;
}

export function normalizeCategoryFromApi(raw: Record<string, unknown>): Record<string, unknown> & { id: string } {
  const id = getEntityId(raw as { id?: string; _id?: string });
  const parentRaw = raw.parent;
  let parent: string | null = null;
  if (parentRaw != null && parentRaw !== '') {
    parent =
      typeof parentRaw === 'object'
        ? getEntityId(parentRaw as { id?: string; _id?: string }) || null
        : String(parentRaw);
  }

  const attributes = Array.isArray(raw.attributes)
    ? raw.attributes.map((row: Record<string, unknown>) => {
        const attrRef = row.attribute;
        const attributeId =
          typeof attrRef === 'object' && attrRef !== null
            ? getEntityId(attrRef as { id?: string; _id?: string })
            : String(attrRef ?? '');
        return {
          ...row,
          attribute: attributeId,
        };
      })
    : [];

  return { ...raw, id, parent, attributes };
}

export function normalizeBrandFromApi(raw: Record<string, unknown>): Record<string, unknown> & { id: string } {
  return { ...raw, id: getEntityId(raw as { id?: string; _id?: string }) };
}

export function normalizeAttributeFromApi(raw: Record<string, unknown>): Record<string, unknown> & { id: string } {
  const id = getEntityId(raw as { id?: string; _id?: string });
  return { ...raw, id, _id: id };
}

export async function fetchCategories<T = Record<string, unknown>>() {
  const response = await api.get('/admin/categories');
  const list = (response.data?.data || []) as Record<string, unknown>[];
  return list.map((item) => normalizeCategoryFromApi(item)) as T[];
}

export async function saveCategory(payload: CategoryPayload, id?: string) {
  if (id) {
    return api.put(`/admin/categories/${id}`, payload);
  }
  return api.post('/admin/categories', payload);
}

export async function deleteCategory(id: string) {
  return api.delete(`/admin/categories/${id}`);
}

export async function fetchBrands<T = Record<string, unknown>>() {
  const response = await api.get('/admin/brands');
  const list = (response.data?.data || []) as Record<string, unknown>[];
  return list.map((item) => normalizeBrandFromApi(item)) as T[];
}

export async function saveBrand(payload: BrandPayload, id?: string) {
  if (id) {
    return api.put(`/admin/brands/${id}`, payload);
  }
  return api.post('/admin/brands', payload);
}

export async function deleteBrand(id: string) {
  return api.delete(`/admin/brands/${id}`);
}

export async function fetchAttributes<T = Record<string, unknown>>(activeOnly = false) {
  const url = activeOnly ? '/admin/attributes/active' : '/admin/attributes';
  const response = await api.get(url);
  const list = (response.data?.data || []) as Record<string, unknown>[];
  return list.map((item) => normalizeAttributeFromApi(item)) as T[];
}

export function slugifyAttributeName(displayName: string): string {
  return displayName
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

export async function saveAttribute(payload: AttributePayload, id?: string) {
  if (id) {
    return api.put(`/admin/attributes/${id}`, payload);
  }
  return api.post('/admin/attributes', payload);
}

export async function deleteAttribute(id: string) {
  return api.delete(`/admin/attributes/${id}`);
}
